import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mtop-10"
}
const _hoisted_2 = { class: "main-m mleft-02" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  key: 0,
  class: "col gap-04 w-full"
}
const _hoisted_6 = { class: "w-full mright-04" }
const _hoisted_7 = {
  key: 1,
  class: "row w-full mtop-15"
}
const _hoisted_8 = { class: "row-end w-full align" }
const _hoisted_9 = {
  key: 1,
  class: "flex-col h-full",
  style: {"display":"flex","justify-content":"flex-end"}
}
const _hoisted_10 = { class: "item-header w-full mtop-10" }
const _hoisted_11 = { class: "item-center row w-full" }
const _hoisted_12 = { class: "row w-full" }
const _hoisted_13 = { class: "mleft-05" }
const _hoisted_14 = { class: "mtop-15 w-full" }
const _hoisted_15 = {
  key: 1,
  class: "mtop-10"
}
const _hoisted_16 = { class: "main-m mleft-02" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ApiError, FinancialChannelResponse, FinancialGroupResponse, AddOrUpdateFinancialChannelRequest, FinancialGroupPermissionResponse, UpdatePersonPermissionRequest, PersonApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { useAppUserStore } from '@/stores/appUser';
import { toastError, toastSuccess } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import EsFinancialChannelSettings from '@/components/settings/accounting/esFinancialChannelSettings.vue';
import { ItemSelect, UpdateItemName } from '@/models/share.model';
import EsNameForm from '@/components/form/esNameForm.vue';
import EsFinancialChannelForm from '@/components/form/accounting/esFinancialChannelForm.vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import SelectButton from 'primevue/selectbutton';


export default /*@__PURE__*/_defineComponent({
  __name: 'esFinancialGroupDetailSettings',
  props: {
    selectedFinancialGroup: {},
    isAllPermissionUpdated: { type: Boolean },
    selectedFinancialGroupActive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const personApi = new PersonApi(medServerApiConfig);

const toast = useToast();
const { t } = useI18n();
const appUserStore = useAppUserStore();
const financialGroupDetail = ref<FinancialGroupResponse>(undefined);
const isNameFormOpened = ref<boolean>(false);
const isAddChannelFormOpened = ref<boolean>(false);
const addOrUpdateFinancialChannelRequest = ref<AddOrUpdateFinancialChannelRequest>(undefined);
const selectedFinancialChannelId = ref<number>(undefined);

const updateName = ref<UpdateItemName>({
    itemUuid : '',
    name: ''
});

const tierOptions = [
    { label: t('settings.label.accounting.financialGroup.financialChannel.permissions.payment'), value: 'PAYMENT' },
    { label: t('settings.label.accounting.financialGroup.financialChannel.permissions.edition'), value: 'EDITION' },
    { label: t('settings.label.accounting.financialGroup.financialChannel.permissions.creation'), value: 'CREATION' }
];

const TIER_PERMISSIONS: Record<string, UpdatePersonPermissionRequest['permissions']> = {
    PAYMENT: ['ADD_PAYMENT_TRANSACTIONS'],
    EDITION: ['ADD_PAYMENT_TRANSACTIONS', 'MODIFY_TRANSACTIONS', 'ADD_CANCEL_TRANSACTIONS'],
    CREATION: ['ADD_PAYMENT_TRANSACTIONS', 'MODIFY_TRANSACTIONS', 'ADD_CANCEL_TRANSACTIONS', 'CREATE_BILLS', 'EXPORT_BILLS']
};

const getActiveTier = (permissions: string[]): string | null => {
    if (!permissions?.length) return null;
    if (permissions.includes('CREATE_BILLS')) return 'CREATION';
    if (permissions.includes('MODIFY_TRANSACTIONS')) return 'EDITION';
    return 'PAYMENT';
};

const getDisplayTiers = (permissions: string[]): string[] => {
    const tier = getActiveTier(permissions);
    if (tier === 'CREATION') return ['PAYMENT', 'EDITION', 'CREATION'];
    if (tier === 'EDITION') return ['PAYMENT', 'EDITION'];
    if (tier === 'PAYMENT') return ['PAYMENT'];
    return [];
};

const handleTierClick = (permission: FinancialGroupPermissionResponse, newDisplay: string[]) => {
    const current = getActiveTier(permission.permissions);
    const oldDisplay = getDisplayTiers(permission.permissions);
    // Find what was clicked — what changed between old and new
    const clicked = oldDisplay.find(v => !newDisplay.includes(v)) 
                 ?? newDisplay.find(v => !oldDisplay.includes(v));

    if (!clicked) return;
    // If clicking current active tier → downgrade once
    if (clicked === current) {
        if (current === 'PAYMENT') updatePersonPermission(permission, null);
        else if (current === 'EDITION') updatePersonPermission(permission, 'PAYMENT');
        else if (current === 'CREATION') updatePersonPermission(permission, 'EDITION');
        return;
    }
    // Otherwise → jump directly to clicked tier
    updatePersonPermission(permission, clicked);
};

const currentPersonId = computed(() => {
  if (!appUserStore.currentAppUserData || !appUserStore.currentAppUserData.healthGroupMemberships) { return null; }
  return appUserStore.currentAppUserData.healthGroupMemberships[0].pwr.personWithRoleId;
});

const currInstituteId = computed((): string => {
  if (!appUserStore.currentHealthGroupMembership) { return ''; }
  return appUserStore.currentHealthGroupMembership.instituteWithRole.instituteWithRoleId;
});

const isOwner = computed((): boolean => {
  return financialGroupDetail.value?.owner === currentPersonId.value;
});

const getFinancialGroupById = async () => {
    if (props.selectedFinancialGroup?.id && currInstituteId.value) {
        personApi.getFinancialGroupById({
            financialGroupId: Number(props.selectedFinancialGroup.id),
            personId: currentPersonId.value,
            instituteId: currInstituteId.value
        }).then((result: FinancialGroupResponse) => {
            financialGroupDetail.value = result;
        }).catch((error: ApiError) => {
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
            });
        });
    }
};
const updatePersonPermission = (permission: FinancialGroupPermissionResponse, selectedTier: string | null) => {
    if (props.selectedFinancialGroup?.id && permission?.personId) {
        const permissions = selectedTier ? TIER_PERMISSIONS[selectedTier] : [];
        // Optimistic update — update local state immediately to avoid flicker
        permission.permissions = permissions as string[];
        personApi.updatePersonPermissionByFinancialGroup({
            personId: permission.personId,
            financialGroupId: Number(props.selectedFinancialGroup.id),
            updatePersonPermissionRequest: { permissions }
        }).then(async () => {
            toastSuccess(toast, {
                summary: t('common.toast.success.update'),
                detail: t('settings.label.accounting.financialGroup.message.success.update')
            });
            await getFinancialGroupById();
        }).catch(async (error: ApiError) => {
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
            });
            // Revert optimistic update on error
            await getFinancialGroupById();
        });
    }
};

const updateActiveChannel = (financialChannelId: number) => {
    if (financialChannelId && props.selectedFinancialGroup?.id && currentPersonId.value) {
      personApi.updateActiveFinancialChannel({
          personId: currentPersonId.value,
          financialGroupId: Number(props.selectedFinancialGroup.id),
          financialChannelId: Number(financialChannelId)
      }).then(async () => {
          toastSuccess(toast, {
              summary: t('common.toast.success.update'),
              detail: t('settings.label.accounting.financialGroup.financialChannel.update.message.success')
          });
          await getFinancialGroupById();
          isAddChannelFormOpened.value = false;
          selectedFinancialChannelId.value = undefined;
          appUserStore.setRefreshAccounting(true);
      }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });
      });
    }
}

const updateChannel = (addOrUpdateFinancialChannelRequest: AddOrUpdateFinancialChannelRequest, financialChannelId: number) => {
    if (financialChannelId && props.selectedFinancialGroup?.id && currentPersonId.value) {
      personApi.updateFinancialChannel({
          personId: currentPersonId.value,
          financialGroupId: Number(props.selectedFinancialGroup.id),
          financialChannelId: Number(financialChannelId),
          addOrUpdateFinancialChannelRequest
      }).then(async () => {
          toastSuccess(toast, {
              summary: t('common.toast.success.update'),
              detail: t('settings.label.accounting.financialGroup.financialChannel.update.message.success')
          });
          await getFinancialGroupById();
          isAddChannelFormOpened.value = false;
          selectedFinancialChannelId.value = undefined;
          appUserStore.setRefreshAccounting(true);
      }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });
          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
      });
  }
};

const openTitleForm = (financialChannel: FinancialChannelResponse) => {
    selectedFinancialChannelId.value = financialChannel.id;
    addOrUpdateFinancialChannelRequest.value = financialChannel;
    updateName.value.name = financialChannel.title;
    isNameFormOpened.value = true;
};

const closeNameFormModal = async (newName: string) => {
    if (newName) {
      addOrUpdateFinancialChannelRequest.value.title = newName;
      await updateChannel(addOrUpdateFinancialChannelRequest.value, selectedFinancialChannelId.value);
      addOrUpdateFinancialChannelRequest.value = undefined;
    }
    updateName.value = { itemUuid: '', name: '' };
    isNameFormOpened.value = false;
};

const openAddChannelForm = () => {
    isAddChannelFormOpened.value = true;
};

const closeAddChannelForm = async (addOrUpdateFinancialChannelRequest: AddOrUpdateFinancialChannelRequest) => {
  if (addOrUpdateFinancialChannelRequest && props.selectedFinancialGroup?.id && currentPersonId.value) {
      await personApi.addFinancialChannel({
          personId: currentPersonId.value,
          financialGroupId: Number(props.selectedFinancialGroup.id),
          addOrUpdateFinancialChannelRequest
      }).then(async () => {
          toastSuccess(toast, {
              summary: t('common.toast.success.add'),
              detail: t('settings.label.accounting.financialGroup.financialChannel.add.message.success')
          });
          await getFinancialGroupById();
          isAddChannelFormOpened.value = false;
          appUserStore.setRefreshAccounting(true);
      }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });
          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
      });
  } else {
    isAddChannelFormOpened.value = false;
  }
}

watch(
  () => props.selectedFinancialGroup,
  async () => {
    if (props.selectedFinancialGroup) {
        await getFinancialGroupById();
    }
  }
);

watch(
  () => props.isAllPermissionUpdated,
  async () => {
    await getFinancialGroupById();
  }
);

return (_ctx: any,_cache: any) => {
  const _component_es_button = _resolveComponent("es-button")!
  const _component_es_button_icon = _resolveComponent("es-button-icon")!

  return (!_ctx.selectedFinancialGroupActive)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(t)('settings.label.accounting.financialGroup.unactive')), 1)
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_3, [
        (_ctx.selectedFinancialGroup)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (isOwner.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(_unref(Accordion), {
                      class: "w-full mtop-15 es-permission-container",
                      value: "0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(AccordionPanel), { value: "0" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(AccordionHeader), null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(t)('settings.label.accounting.financialGroup.financialChannel.permissions.title')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(AccordionContent), null, {
                              default: _withCtx(() => [
                                (financialGroupDetail.value?.permissions && financialGroupDetail.value.permissions.length > 0)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(financialGroupDetail.value.permissions, (permission) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                          class: "row-space-between w-full mtop-10",
                                          key: permission.id
                                        }, [
                                          _createElementVNode("span", _hoisted_6, _toDisplayString(permission.name), 1),
                                          (_openBlock(), _createBlock(_unref(SelectButton), {
                                            key: getDisplayTiers(permission.permissions).join(','),
                                            "model-value": getDisplayTiers(permission.permissions),
                                            options: tierOptions,
                                            "option-label": "label",
                                            "option-value": "value",
                                            multiple: "",
                                            "onUpdate:modelValue": ($event: any) => (handleTierClick(permission, $event)),
                                            style: {"width":"fit-content","white-space":"nowrap"}
                                          }, null, 8, ["model-value", "onUpdate:modelValue"]))
                                        ]))
                                      }), 128))
                                    ]))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_unref(t)('settings.label.main.permissions.empty')), 1))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _cache[3] || (_cache[3] = _createElementVNode("hr", { class: "rod-horizontal-small" }, null, -1))
                  ], 64))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_8, [
                (_ctx.selectedFinancialGroup && isOwner.value)
                  ? (_openBlock(), _createBlock(_component_es_button, {
                      key: 0,
                      widthClass: "w-full",
                      label: _unref(t)('settings.label.accounting.financialGroup.financialChannel.add.action'),
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddChannelForm())),
                      "is-primary": true,
                      "is-linked": false
                    }, null, 8, ["label"]))
                  : _createCommentVNode("", true)
              ]),
              (financialGroupDetail.value?.financialChannels)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(financialGroupDetail.value.financialChannels, (item, index) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("div", _hoisted_12, [
                              _createElementVNode("h4", _hoisted_13, _toDisplayString(item.title), 1),
                              (isOwner.value)
                                ? (_openBlock(), _createBlock(_component_es_button_icon, {
                                    key: 0,
                                    class: "mleft-05 pad-m",
                                    icon: "pencil_fill",
                                    folder: "shareIcons",
                                    styles: "img-grey",
                                    onClick: ($event: any) => (openTitleForm(item))
                                  }, null, 8, ["onClick"]))
                                : _createCommentVNode("", true)
                            ])
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                          _createVNode(EsFinancialChannelSettings, {
                            "financial-channel": item,
                            onUpdateChannel: ($event: any) => (updateChannel($event, item.id)),
                            onUpdateActiveChannel: ($event: any) => (updateActiveChannel(item.id)),
                            "is-owner": isOwner.value
                          }, null, 8, ["financial-channel", "onUpdateChannel", "onUpdateActiveChannel", "is-owner"])
                        ])
                      ], 64))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createElementVNode("span", _hoisted_16, _toDisplayString(_unref(t)('settings.label.accounting.financialGroup.financialChannel.empty')), 1)
            ])),
        _createVNode(EsNameForm, {
          isOpen: isNameFormOpened.value,
          header: _unref(t)('settings.label.accounting.financialGroup.financialChannel.update.title'),
          label: _unref(t)('settings.label.accounting.financialGroup.financialChannel.data.title'),
          name: updateName.value.name,
          updateMethod: closeNameFormModal,
          onModalClose: _cache[1] || (_cache[1] = ($event: any) => (isNameFormOpened.value = false))
        }, null, 8, ["isOpen", "header", "label", "name"]),
        _createVNode(EsFinancialChannelForm, {
          isOpen: isAddChannelFormOpened.value,
          header: _unref(t)('settings.label.accounting.financialGroup.financialChannel.add.header'),
          "confirm-method": closeAddChannelForm,
          onModalClose: _cache[2] || (_cache[2] = ($event: any) => {isAddChannelFormOpened.value = false;})
        }, null, 8, ["isOpen", "header"])
      ]))
}
}

})